import { useState, useEffect, useRef, useCallback, createContext } from 'react';
import { supabase } from './supabaseClient';

// ══════════════════════════════════════════════════════════════
// Модель:
// - session_id (per tab, in-memory random) — конкретна вкладка
// - client_id (per browser, LS) — стабільний ідентифікатор браузера
// - user_profiles.active_session_id + active_client_id + active_session_last_write_at — серверна правда
//
// Правила:
// 1. Якщо чинний active_client_id == мій client_id → беру силоміць (той самий браузер, F5 або нова вкладка)
// 2. Якщо чинна активна сесія не робила save > 15 хв → беру
// 3. Інакше — пасивний режим
//
// BroadcastChannel — для того щоб дві вкладки одного браузера не боролися
// (вторинна вкладка тихо йде в пасив без модалок)
// ══════════════════════════════════════════════════════════════

const COOLDOWN_MINUTES = 15;
const CLIENT_ID_KEY = 'lihtar_client_id';
const BC_INIT_WAIT_MS = 250;

function ensureClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = crypto?.randomUUID?.() || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function newSessionId() {
  return crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const ActiveSessionContext = createContext({
  isActive: true,
  isPassive: false,
  status: 'active',
  modalToShow: null,
  cooldownRemainingMin: 0,
  claimActivity: async () => {},
  notifyWrite: async () => {},
  dismissModal: () => {},
});

export function useActiveSession(userId) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'active' | 'passive'
  const [modalToShow, setModalToShow] = useState(null); // 'auto-active' | 'auto-passive' | 'cooldown' | null
  const [cooldownRemainingMin, setCooldownRemainingMin] = useState(0);

  const sessionIdRef = useRef(null);
  const clientIdRef = useRef(null);
  const bcRef = useRef(null);
  const statusRef = useRef('checking');

  // Init identity once (per tab lifecycle)
  if (!sessionIdRef.current) sessionIdRef.current = newSessionId();
  if (!clientIdRef.current) clientIdRef.current = ensureClientId();

  useEffect(() => { statusRef.current = status; }, [status]);

  // Upsert my session as active (used by checkAndDecide + claimActivity)
  // ВАЖЛИВО: last_write_at скидаємо в NULL при take-over — cooldown починається
  // ЛИШЕ від першого реального save (через notifyWrite). Без цього кожне
  // відкриття блокувало інших на 15 хв навіть якщо нічого не робили.
  const upsertActive = useCallback(async () => {
    if (!userId) return;
    const payload = {
      user_id: userId,
      active_session_id: sessionIdRef.current,
      active_client_id: clientIdRef.current,
      active_session_last_write_at: null,
    };
    try {
      const { error } = await supabase.from('user_profiles').upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;
    } catch {
      // Fallback: manual insert/update
      try {
        const { data: existing } = await supabase
          .from('user_profiles')
          .select('user_id')
          .eq('user_id', userId)
          .maybeSingle();
        if (existing) {
          await supabase.from('user_profiles').update({
            active_session_id: sessionIdRef.current,
            active_client_id: clientIdRef.current,
            active_session_last_write_at: null,
          }).eq('user_id', userId);
        } else {
          await supabase.from('user_profiles').insert(payload);
        }
      } catch (e) { console.error('upsertActive fallback:', e); }
    }
  }, [userId]);

  // Fetch server state and decide status
  const checkAndDecide = useCallback(async () => {
    if (!userId) return;
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('active_session_id, active_client_id, active_session_last_write_at')
        .eq('user_id', userId)
        .maybeSingle();

      const now = Date.now();
      const lastWriteAt = profile?.active_session_last_write_at
        ? new Date(profile.active_session_last_write_at).getTime()
        : 0;
      const activeClientId = profile?.active_client_id;
      const activeSessionId = profile?.active_session_id;

      const isMineByClient = !!activeClientId && activeClientId === clientIdRef.current;
      const noActive = !activeSessionId;
      const cooldownExpired = lastWriteAt === 0 || (now - lastWriteAt) >= COOLDOWN_MINUTES * 60 * 1000;
      const hadOtherActiveRecent = !!activeSessionId && !isMineByClient && !cooldownExpired;

      if (isMineByClient || noActive) {
        // Same browser (F5, нова вкладка) АБО порожньо — беру тихо
        await upsertActive();
        setStatus('active');
        setModalToShow(null);
      } else if (cooldownExpired) {
        // Інший браузер, але давно не писав — беру, показую info
        await upsertActive();
        setStatus('active');
        setModalToShow('auto-active');
      } else {
        // Хтось інший активний і недавно писав — пасивний
        setStatus('passive');
        setModalToShow('auto-passive');
      }
      // hadOtherActiveRecent тут не використовується явно, але залишаю змінну на випадок майбутньої логіки
      void hadOtherActiveRecent;
    } catch (e) {
      console.error('checkAndDecide error:', e);
      // Fail-safe: passive щоб не збити чужу роботу
      setStatus('passive');
    }
  }, [userId, upsertActive]);

  // Init: BroadcastChannel election + check
  useEffect(() => {
    if (!userId) return;

    let sawActiveTab = false;
    let cancelled = false;

    const bc = (typeof BroadcastChannel !== 'undefined')
      ? new BroadcastChannel(`lihtar-active-${userId}`)
      : null;
    bcRef.current = bc;

    const messageHandler = (event) => {
      const data = event.data;
      if (!data || data.sessionId === sessionIdRef.current) return;

      if (data.type === 'ping') {
        // Нова вкладка питає — відповідаємо якщо ми активні
        if (statusRef.current === 'active' && bc) {
          bc.postMessage({ type: 'pong-active', sessionId: sessionIdRef.current });
        }
      } else if (data.type === 'pong-active') {
        // Інша вкладка того ж браузера вже активна — тихий пасив, без модалок
        sawActiveTab = true;
        setStatus('passive');
        setModalToShow(null);
      } else if (data.type === 'takeover') {
        // Хтось іншого браузера/девайсу забрав активність — показуємо модалку
        setStatus('passive');
        setModalToShow('auto-passive');
      }
    };

    if (bc) {
      bc.addEventListener('message', messageHandler);
      bc.postMessage({ type: 'ping', sessionId: sessionIdRef.current });
    }

    // Чекаємо трохи щоб інші вкладки встигли відповісти, тоді перевіряємо сервер
    const initTimer = setTimeout(() => {
      if (cancelled || sawActiveTab) return;
      checkAndDecide();
    }, BC_INIT_WAIT_MS);

    return () => {
      cancelled = true;
      clearTimeout(initTimer);
      if (bc) {
        bc.removeEventListener('message', messageHandler);
        bc.close();
      }
      bcRef.current = null;
    };
  }, [userId, checkAndDecide]);

  // Викликається з banner при кліку "Стати активним"
  const claimActivity = useCallback(async () => {
    if (!userId) return { success: false };
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('active_session_id, active_client_id, active_session_last_write_at')
        .eq('user_id', userId)
        .maybeSingle();

      const now = Date.now();
      const lastWriteAt = profile?.active_session_last_write_at
        ? new Date(profile.active_session_last_write_at).getTime()
        : 0;
      const activeClientId = profile?.active_client_id;
      const isMineByClient = !!activeClientId && activeClientId === clientIdRef.current;
      const msLeft = COOLDOWN_MINUTES * 60 * 1000 - (now - lastWriteAt);
      const cooldownActive = msLeft > 0;

      // Своєму браузеру завжди даємо
      if (!isMineByClient && cooldownActive) {
        const remainingMin = Math.max(1, Math.ceil(msLeft / 60000));
        setCooldownRemainingMin(remainingMin);
        setModalToShow('cooldown');
        return { success: false, cooldownRemainingMin: remainingMin };
      }

      await upsertActive();
      // Сигнал іншим вкладкам того ж браузера
      if (bcRef.current) {
        bcRef.current.postMessage({ type: 'takeover', sessionId: sessionIdRef.current });
      }
      // Reload щоб гарантовано підтягнути свіжі дані
      window.location.reload();
      return { success: true };
    } catch (e) {
      console.error('claimActivity error:', e);
      return { success: false, error: e };
    }
  }, [userId, upsertActive]);

  // Викликається з saveData коли активна сесія робить збереження
  // Оновлює last_write_at + перевіряє чи ми все ще активні
  const notifyWrite = useCallback(async () => {
    if (!userId || statusRef.current !== 'active') return;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ active_session_last_write_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('active_session_id', sessionIdRef.current)
        .select();

      if (!error && (!data || data.length === 0)) {
        // Ми більше не активні — хтось перехопив
        setStatus('passive');
        setModalToShow('auto-passive');
      }
    } catch (e) { console.error('notifyWrite:', e); }
  }, [userId]);

  const dismissModal = useCallback(() => {
    setModalToShow(null);
  }, []);

  // Polling — раз на 10 сек активна сесія перевіряє чи вона все ще активна
  // (щоб швидко помітити перехоплення з іншого девайсу і показати banner)
  useEffect(() => {
    if (!userId) return;
    const intervalId = setInterval(async () => {
      if (statusRef.current !== 'active') return;
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('active_session_id')
          .eq('user_id', userId)
          .maybeSingle();
        const stillMine = profile?.active_session_id === sessionIdRef.current;
        if (!stillMine) {
          setStatus('passive');
          setModalToShow('auto-passive');
        }
      } catch { /* ignore transient errors */ }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [userId]);

  // Cleanup при закритті вкладки — звільняємо активність, щоб інша сесія не чекала 15 хв
  useEffect(() => {
    if (!userId) return;
    const handleUnload = () => {
      if (statusRef.current !== 'active') return;
      try {
        // Знаходимо auth token з LS (Supabase зберігає там)
        const authKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (!authKey) return;
        const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
        const token = authData?.access_token;
        if (!token) return;

        const SUPABASE_URL = 'https://jkouufahpliqgyvcmbwb.supabase.co';
        const ANON_KEY = 'sb_publishable_3mx9WR7tJggjnVTX5D6OuA_HQ23bz38';

        // PATCH з keepalive=true — браузер гарантує відправку навіть при unload
        // Умова eq на active_session_id — щоб не зламати чужу активну сесію якщо вона встигла нас перехопити
        fetch(
          `${SUPABASE_URL}/rest/v1/user_profiles?user_id=eq.${encodeURIComponent(userId)}&active_session_id=eq.${encodeURIComponent(sessionIdRef.current)}`,
          {
            method: 'PATCH',
            headers: {
              'apikey': ANON_KEY,
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Prefer': 'return=minimal',
            },
            body: JSON.stringify({
              active_session_id: null,
              active_client_id: null,
              active_session_last_write_at: null,
            }),
            keepalive: true,
          }
        ).catch(() => {});
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload); // мобайл, iOS Safari
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [userId]);

  return {
    isActive: status === 'active',
    isPassive: status === 'passive',
    status,
    modalToShow,
    cooldownRemainingMin,
    claimActivity,
    notifyWrite,
    dismissModal,
  };
}
